import { CommonModule } from '@angular/common';
import { Component, effect, inject, signal } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { CompanyContextService } from '../../../../core/services/company-context.service';
import {
  CreateSmartCullUserRequest,
  SmartCullUserItem,
  UpdateSmartCullUserRequest
} from '../../data/models/smart-cull-user.models';
import { SmartCullService } from '../../data/services/smart-cull.service';
import { GlobalAlertService } from '../../../../core/services/global-alert.service';

@Component({
  selector: 'app-smart-cull',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './smart-cull.html',
  styleUrl: './smart-cull.scss'
})
export class SmartCull {
  private readonly smartCullService = inject(SmartCullService);
  private readonly companyContextService = inject(CompanyContextService);
  private readonly fb = inject(FormBuilder);
  private readonly globalAlertService = inject(GlobalAlertService);

  searchTerm = '';
  private searchSubject = new Subject<string>();

  users: SmartCullUserItem[] = [];
  totalCount = 0;
  pageNumber = 1;
  pageSize = 20;
  totalPages = 0;

  isLoading = false;
  errorMessage = '';

  isModalOpen = false;
  isSaving = false;
  isEditMode = false;
  editingUserId: number | null = null;
  deletingUserId: number | null = null;
  isResettingUserId: number | null = null;

  isDeleteModalOpen = signal(false);
  deletingUser: SmartCullUserItem | null = null;

  isResetModalOpen = signal(false);
  resettingUser: SmartCullUserItem | null = null;

  readonly statusOptions = ['Active', 'Inactive'];

  readonly userForm = this.fb.group({
    firstName: ['', [Validators.required]],
    lastName: ['', [Validators.required]],
    phoneNumber: ['', [Validators.required]],
    email: ['', [Validators.required, Validators.email]],
    username: ['', [Validators.required]],
    status: ['Active', [Validators.required]],
    addressLine1: ['', [Validators.required]],
    addressLine2: [''],
    city: ['', [Validators.required]],
    state: ['', [Validators.required]],
    zipCode: ['', [Validators.required]],
    country: ['USA', [Validators.required]]
  });

  constructor() {
    effect(() => {
      // The user noted that smart-cull-users is independent of companyId
      // and suggested defaulting to 1 or omitting it. The endpoint doesn't accept companyId anyway.
      // E.g., `this.selectedCompanyId()` triggers effect re-evaluation if company changes.
      this.companyContextService.selectedCompanyId();
      this.pageNumber = 1;
      this.loadUsers();
    });

    this.searchSubject
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe((term) => {
        this.searchTerm = term;
        this.pageNumber = 1;
        this.loadUsers();
      });
  }

  onSearchChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchSubject.next(input.value);
  }

  loadUsers(): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.smartCullService
      .getSmartCullUsers(this.pageNumber, this.pageSize, this.searchTerm)
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.users = response.data.users;
            this.totalCount = response.data.totalCount;
            this.totalPages = response.data.totalPages || Math.ceil(this.totalCount / this.pageSize);
          } else {
            this.errorMessage = response.message || 'Failed to load smart cull users.';
            this.users = [];
          }
          this.isLoading = false;
        },
        error: (err) => {
          this.errorMessage = err?.error?.message || 'Failed to load smart cull users.';
          this.users = [];
          this.isLoading = false;
        }
      });
  }

  // --- Pagination Methods ---
  nextPage(): void {
    if (this.pageNumber < this.totalPages) {
      this.pageNumber++;
      this.loadUsers();
    }
  }

  previousPage(): void {
    if (this.pageNumber > 1) {
      this.pageNumber--;
      this.loadUsers();
    }
  }

  openCreateModal(): void {
    this.isEditMode = false;
    this.editingUserId = null;

    this.userForm.reset({
      firstName: '',
      lastName: '',
      phoneNumber: '',
      email: '',
      username: '',
      status: 'Active',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'USA'
    });

    this.errorMessage = '';
    this.isModalOpen = true;
  }

  openEditModal(user: SmartCullUserItem): void {
    this.isEditMode = true;
    this.editingUserId = user.smartCullUserId;

    this.userForm.reset({
      firstName: user.firstName,
      lastName: user.lastName,
      phoneNumber: user.phoneNumber,
      email: user.email,
      username: user.username,
      status: user.isActive ? 'Active' : 'Inactive',
      addressLine1: user.addressLine1 || '',
      addressLine2: user.addressLine2 || '',
      city: user.city || '',
      state: user.state || '',
      zipCode: user.zipCode || '',
      country: user.country || 'USA'
    });

    this.errorMessage = '';
    this.isModalOpen = true;
  }

  closeModal(): void {
    this.isModalOpen = false;
    this.isEditMode = false;
    this.editingUserId = null;
    this.userForm.markAsPristine();
    this.userForm.markAsUntouched();
  }

  onCreateUser(): void {
    this.openCreateModal();
  }

  saveUser(): void {
    if (this.userForm.invalid) {
      this.userForm.markAllAsTouched();
      return;
    }

    this.isSaving = true;
    this.errorMessage = '';

    const formValue = this.userForm.getRawValue();
    const isActive = (formValue.status ?? 'Active') === 'Active';

    if (this.isEditMode && this.editingUserId !== null) {
      const payload: UpdateSmartCullUserRequest = {
        firstName: formValue.firstName ?? '',
        lastName: formValue.lastName ?? '',
        phoneNumber: formValue.phoneNumber ?? '',
        email: formValue.email ?? '',
        username: formValue.username ?? '',
        isActive: isActive,
        addressLine1: formValue.addressLine1 ?? '',
        addressLine2: formValue.addressLine2 ?? '',
        city: formValue.city ?? '',
        state: formValue.state ?? '',
        zipCode: formValue.zipCode ?? '',
        country: formValue.country ?? ''
      };

      this.smartCullService.updateSmartCullUser(this.editingUserId, payload).subscribe({
        next: (response) => {
          if (response.success) {
            this.loadUsers();
            this.closeModal();
          } else {
            this.errorMessage = response.message || 'Failed to update smart cull user.';
          }
          this.isSaving = false;
        },
        error: (err) => {
          this.isSaving = false;
          this.errorMessage = err?.error?.message || 'Failed to update smart cull user.';
        }
      });

      return;
    }

    const payload: CreateSmartCullUserRequest = {
      firstName: formValue.firstName ?? '',
      lastName: formValue.lastName ?? '',
      phoneNumber: formValue.phoneNumber ?? '',
      email: formValue.email ?? '',
      username: formValue.username ?? '',
      isActive: isActive,
      addressLine1: formValue.addressLine1 ?? '',
      addressLine2: formValue.addressLine2 ?? '',
      city: formValue.city ?? '',
      state: formValue.state ?? '',
      zipCode: formValue.zipCode ?? '',
      country: formValue.country ?? ''
    };

    this.smartCullService.createSmartCullUser(payload).subscribe({
      next: (response) => {
        if (response.success) {
          this.pageNumber = 1;
          this.loadUsers();
          this.closeModal();
        } else {
          this.errorMessage = response.message || 'Failed to create smart cull user.';
        }
        this.isSaving = false;
      },
      error: (err) => {
        this.isSaving = false;
        this.errorMessage = err?.error?.message || 'Failed to create smart cull user.';
      }
    });
  }

  openDeleteModal(user: SmartCullUserItem): void {
    this.deletingUser = user;
    this.isDeleteModalOpen.set(true);
  }

  closeDeleteModal(): void {
    this.isDeleteModalOpen.set(false);
    this.deletingUser = null;
  }

  confirmDeleteUser(): void {
    if (!this.deletingUser) return;

    const userId = this.deletingUser.smartCullUserId;
    this.deletingUserId = userId;

    this.smartCullService.deleteSmartCullUser(userId).subscribe({
      next: (response) => {
        if (response.success) {
          if (this.users.length === 1 && this.pageNumber > 1) {
            this.pageNumber--;
          }
          this.loadUsers();
          this.closeDeleteModal();
        } else {
          this.errorMessage = response.message || 'Failed to delete smart cull user.';
          this.closeDeleteModal();
        }
        this.deletingUserId = null;
      },
      error: (err) => {
        this.errorMessage = err?.error?.message || 'Failed to delete smart cull user.';
        this.deletingUserId = null;
        this.closeDeleteModal();
      }
    });
  }

  onDeleteUser(user: SmartCullUserItem): void {
    this.openDeleteModal(user);
  }

  onResetPassword(user: SmartCullUserItem): void {
    this.resettingUser = user;
    this.isResetModalOpen.set(true);
  }

  closeResetModal(): void {
    this.isResetModalOpen.set(false);
    this.resettingUser = null;
  }

  confirmResetPassword(): void {
    if (!this.resettingUser) return;

    const user = this.resettingUser;
    this.isResettingUserId = user.smartCullUserId;

    this.smartCullService.resetSmartCullUserPassword(user.smartCullUserId).subscribe({
      next: (response) => {
        this.isResettingUserId = null;
        this.closeResetModal();
        if (response.success) {
          this.globalAlertService.showSuccess(response.message || 'Password reset successfully.');
        } else {
          this.globalAlertService.showError(response.message || 'Failed to reset password.');
        }
      },
      error: (err) => {
        this.isResettingUserId = null;
        this.closeResetModal();
        this.globalAlertService.showError(err?.error?.message || 'Failed to reset password.');
      }
    });
  }

  get firstNameControl() { return this.userForm.get('firstName'); }
  get lastNameControl() { return this.userForm.get('lastName'); }
  get phoneNumberControl() { return this.userForm.get('phoneNumber'); }
  get emailControl() { return this.userForm.get('email'); }
  get usernameControl() { return this.userForm.get('username'); }
  get addressLine1Control() { return this.userForm.get('addressLine1'); }
  get cityControl() { return this.userForm.get('city'); }
  get stateControl() { return this.userForm.get('state'); }
  get zipCodeControl() { return this.userForm.get('zipCode'); }
  get countryControl() { return this.userForm.get('country'); }
}